<template>
  <div v-if="showTrialStatus" class="relative">
    <!-- Trial days remaining with dropdown -->
    <button
      @click="showDropdown = !showDropdown"
      class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-900 hover:text-primary dark:text-white dark:hover:text-primary"
    >
      <i class="text-orange-500 fas fa-clock"></i>
      {{ remainingDays }} Trial Days Left
      <i class="text-xs fas fa-chevron-down"></i>
    </button>

    <!-- Dropdown menu -->
    <div
      v-if="showDropdown"
      @click.stop
      class="absolute right-0 z-50 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg w-80 dark:bg-gray-800 dark:border-gray-700"
    >
      <div class="p-4">
        <div class="mb-2 text-sm font-semibold text-gray-900 dark:text-white">
          Trial Status
        </div>
        
        <div class="mb-3 text-sm text-gray-600 dark:text-gray-300">
          You have {{ remainingDays }} days remaining in your free trial.
        </div>

        <div class="mb-4">
          <div class="mb-1 text-xs text-gray-500 dark:text-gray-400">
            Trial ends on: {{ trialEndDate }}
          </div>
          <div class="w-full h-2 bg-gray-200 rounded-full dark:bg-gray-700">
            <div 
              class="h-2 transition-all duration-300 rounded-full bg-gradient-to-r from-orange-500 to-red-500"
              :style="{ width: progressPercentage + '%' }"
            ></div>
          </div>
        </div>

        <div class="mb-4 text-xs text-gray-500 dark:text-gray-400">
          <strong>Trial Benefits:</strong>
          <ul class="mt-1 space-y-1 list-disc list-inside">
            <li>Access to all features</li>
            <li>Create and manage gigs</li>
            <li>Apply to opportunities</li>
            <li>Profile visibility (with photo & bio)</li>
          </ul>
        </div>

        <button
          @click="goToUpgrade"
          class="w-full px-4 py-2 font-medium text-white transition-colors rounded-lg bg-primary hover:bg-purple-800"
        >
          Upgrade Now
        </button>
      </div>
    </div>
  </div>

  <!-- Regular upgrade button for non-trial users -->
  <div v-else-if="!isSubscribed">
    <a
      :href="route('subscribe')"
      class="text-gray-900 hover:text-primary dark:text-white dark:hover:text-primary"
    >
      Upgrade
    </a>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { usePage } from '@inertiajs/vue3';

const page = usePage();
const showDropdown = ref(false);

const props = defineProps({
  user: {
    type: Object,
    default: () => ({})
  },
  trialData: {
    type: Object,
    default: () => ({})
  }
});

const isSubscribed = computed(() => page.props.subscribed?.user || false);
const showTrialStatus = computed(() => props.trialData?.hasActiveTrial && props.trialData?.remainingDays > 0);
const remainingDays = computed(() => props.trialData?.remainingDays || 0);
const trialEndDate = computed(() => {
  if (props.trialData?.trialEndsAt) {
    return new Date(props.trialData.trialEndsAt).toLocaleDateString();
  }
  return '';
});

const progressPercentage = computed(() => {
  if (props.trialData?.totalTrialDays && props.trialData?.remainingDays) {
    const elapsed = props.trialData.totalTrialDays - props.trialData.remainingDays;
    return Math.max(0, Math.min(100, (elapsed / props.trialData.totalTrialDays) * 100));
  }
  return 0;
});

const goToUpgrade = () => {
  window.location.href = route('subscribe');
};

const handleClickOutside = (event) => {
  if (!event.target.closest('.relative')) {
    showDropdown.value = false;
  }
};

onMounted(() => {
  // Debug: Check trial data
  if (process.env.NODE_ENV === 'development') {
    if (props.trialData?.hasActiveTrial) {
      console.log('TrialStatus - Active trial with', remainingDays.value, 'days remaining');
    } else {
      console.log('TrialStatus - No active trial, isSubscribed:', isSubscribed.value);
    }
  }
  
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>
